// Pinia store module
// School exclusions counts

/* Generic interface for sbdata-* stores
   - Getter ready() returns true iff data is available
   - Action async load() triggers fetching of data from the server, the
      returned promise is rejected if data cannot be loaded.
*/

import { defineStore } from 'pinia'
import { ref, reactive, computed } from 'vue'

import Papa from 'papaparse'

import { useSbuserStore } from '../auth/sbuser-store.mjs'
import { useSbappStore } from '../store/sbapp-store.mjs'

import { debug } from '@/lib/sb_utility.mjs'


const expectedFieldsStr = 
   '["AsAtDate","NativeID7","SchoolName","Phase","LA","PeriodStartStr",'
   + '"PeriodEndStr","ExclusionsCount","ExcPermCount","ExcTempCount"'
   // We require these fields but others may be provided following these,
   // so received list may continue with ',...' or end with ']'
function schoolComparison(s1, s2){
   if(s1.Phase > s2.Phase) return 1
   else if(s1.Phase < s2.Phase) return -1
   else {
      if(s1.SchoolName > s2.SchoolName) return 1
      else if (s1.SchoolName < s2.SchoolName) return -1
      else return 0
   }
}

/** Process individual school records for display.
 */
function processSchool(sch){
   let res = sch
   // TODO: Add or amend fields as needed
   return res
}

export const useExccountsStore = defineStore('exccounts', ()=>{

   // Standard data page interface
   const dataStatus = ref(0) //2 = loaded, 1 = loading, -1 = error, -2 = not authorized
   // Store-specific data
   const la = ref(false)
   const schools = reactive([])
   const asAtDate = ref(false)
   const periodStart = ref(false)
   const periodEnd = ref(false)

   return {
      // State
      dataStatus,
      la,
      schools,
      asAtDate,
      periodStart, periodEnd,

      // Getters
      loadStatus: computed(()=>dataStatus.value),
      ready: computed(()=>(dataStatus.value>1)),
      schoolsLive: computed(()=>(schools.length)),

      // Actions
      async load(_options){ 
         debug("load data", "countsdata")
         dataStatus.value = 1
         const sbuser = useSbuserStore()
         const sbapp = useSbappStore()
         
         sbuser.registerDataHolder( function(event){
               debug(event, 'countsdata')
               switch(event.type){
                  case 'logout':
                     schools.splice(0)
                     la.value = false
                     dataStatus.value = -2
                     return ""
                  case 'login':
                     dataStatus.value = 0
                     return ""
                  default:
                     debug("Unhandled event", "countsdata")
                     return false
               }
         })

         try {
            debug("Request data", "countsdata")
            la.value = sbuser.org.id
            let datafileName = `SchoolExclusionCounts_${la.value}9999.csv`
            let response = await sbuser.sbdataGet(
               `file/i/${sbapp.filedir}/${datafileName}`
            )
            if(response.ok){
               // Import schools data from file
               debug("Process datafile", "countsdata")
               let body = await response.blob()
               // TODO: separate this section into its own function
               Papa.parse(body, {
                  header: true,
                  complete(result){
                     debug("Store data", "countsdata")
                     try {
                        let fieldsStr = JSON.stringify(result.meta.fields)
                        debug("Fields rcvd: " + fieldsStr, 'countsdata')
                        debug("Fields expd: " + expectedFieldsStr, 'countsdata')
                        if(fieldsStr.startsWith(expectedFieldsStr)){
                           debug("data loaded", "countsdata")
                           result.data.sort(schoolComparison)
                           schools.push( ...
                              result.data
                                 .filter((s)=>(s.NativeID7>0))
                                 .map(processSchool) )
                           // Lookup AsAtDateStr from records
                           let asAtDateStr = schools.reduce(
                              (maxSoFar, current)=>{
                                 let ThisDateStr = current.AsAtDate
                                 if(ThisDateStr>maxSoFar) return ThisDateStr
                                 else return maxSoFar
                              }, "2000-01-01"
                           )
                           asAtDate.value = new Date(asAtDateStr)
                           // Lookup PeriodStartStr from records
                           let periodStartStr = schools.reduce(
                              (minSoFar, current)=>{
                                 let ThisDateStr = current.PeriodStartStr
                                 if(ThisDateStr<minSoFar) return ThisDateStr
                                 else return minSoFar
                              }, "3000-01-01"
                           )
                           periodStart.value = new Date(periodStartStr)
                           // Lookup PeriodEndStr from records
                           let periodEndStr = schools.reduce(
                              (maxSoFar, current)=>{
                                 let ThisDateStr = current.PeriodEndStr
                                 if(ThisDateStr>maxSoFar) return ThisDateStr
                                 else return maxSoFar
                              }, "2000-01-01"
                           )
                           if(periodEndStr<=asAtDateStr)
                              periodEnd.value = new Date(periodEndStr)
                           else periodEnd.value = asAtDate.value

                           dataStatus.value = 2
                        } else {
                           debug("data invalid", "countsdata")
                           dataStatus.value = -3
                        }
                     } catch(e){
                        debug("Error processing statuses", 'countsdata')
                        dataStatus.value = -3
                     }
                  }
               })
            } else if(response.status == 403){
               debug("Not authorized", "countsdata")
               dataStatus.value = -2
            } else if(response.status == 401){
               debug("Not authenticated", "countsdata")
               //TODO: Handle logged out user
               dataStatus.value = -2
            } else {
               debug("Unknown error", "countsdata")
               dataStatus.value = -1
            }
         } catch(err){
            debug(err, "countsdata")
            dataStatus.value = -1
         }
         debug("done", "countsdata")
      },

   }
})
